import React, { useEffect, useState, useMemo } from 'react';
import Swal from 'sweetalert2';
import {
    collection,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    Timestamp,
    where,
    writeBatch,
    updateDoc
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import "../../App.css";
import Menu from '../../panel/Menu';
import moment from 'moment';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TableHead,
    Paper,
    IconButton,
    Stack,
    Pagination,
    Chip,
    InputAdornment,
    Tabs,
    Tab,
    Tooltip,
    Button,
    Circulariser, // Note: using CircularProgress from @mui/material below
    CircularProgress
} from "@mui/material";

import {
    Visibility,
    Close,
    Search as SearchIcon,
    Hotel,
    EventAvailable,
    ExitToApp,
    CheckCircle,
    Warning as WarningIcon,
    SwapHoriz,
    AutoFixHigh
} from "@mui/icons-material";

function Viewreservations() {
    const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
            toast.addEventListener('mouseenter', Swal.stopTimer);
            toast.addEventListener('mouseleave', Swal.resumeTimer);
        }
    });

    const [bookings, setbookings] = useState([]);
    const [propertyId, setpropertyId] = useState(null);
    const [username, setusername] = useState("");
    const [userData, setUserData] = useState(null);
    const [searchText, setsearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage] = useState(10);
    const [currentTab, setCurrentTab] = useState(0); // 0: Live, 1: Upcoming, 2: Checkout
    const [isAutoCorrecting, setIsAutoCorrecting] = useState(false);
    const [hasAttemptedAutoCorrect, setHasAttemptedAutoCorrect] = useState(false);

    // 1️⃣ Authenticate and fetch logged-in user profile details
    useEffect(() => {
        return auth.onAuthStateChanged(async (user) => {
            if (!user) return;
            try {
                const snap = await getDoc(doc(db, "authroles", user.uid));
                if (snap.exists()) {
                    const data = snap.data();
                    setpropertyId(data.propertyId);
                    setusername(data.username || data.name || "Staff");
                    setUserData({ uid: user.uid, ...data });
                }
            } catch (error) {
                console.error("Error fetching user role context:", error);
            }
        });
    }, []);

    useEffect(() => {
        if (!propertyId) return;

        const q = query(
            collection(db, 'reservations'),
            where('propertyId', '==', propertyId),
            where('status', 'in', ['Booked', 'Paid']),
            orderBy('checkindate', 'asc')
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const fetchedBookings = querySnapshot.docs.map(doc => ({
                id: doc.id,
                data: doc.data()
            }));
            setbookings(fetchedBookings);
        }, (error) => {
            console.error("Firestore subscription error:", error);

            if (error.code === 'failed-precondition') {
                const fallbackQuery = query(
                    collection(db, 'reservations'),
                    where('propertyId', '==', propertyId),
                    where('status', 'in', ['Booked', 'Paid'])
                );

                return onSnapshot(fallbackQuery, (fallbackSnapshot) => {
                    const fetchedBookings = fallbackSnapshot.docs.map(doc => ({
                        id: doc.id,
                        data: doc.data()
                    })).sort((a, b) => new Date(a.data.checkindate) - new Date(b.data.checkindate));
                    setbookings(fetchedBookings);
                });
            }

            Toast.fire({
                icon: "error",
                title: "Failed to sync reservations data."
            });
        });

        return unsubscribe;
    }, [propertyId]);

    // Helper to detect if multiple bookings share the same room on the same checkin date
    const conflictBookingIds = useMemo(() => {
        const roomDateMap = {};
        const conflicts = new Set();

        bookings.forEach(b => {
            const checkInDate = b.data?.checkindate;
            const rooms = b.data?.rooms || [];

            rooms.forEach(r => {
                const roomNo = r.roomno;
                if (!roomNo || !checkInDate) return;
                const key = `${checkInDate}_${roomNo}`;

                if (roomDateMap[key]) {
                    conflicts.add(roomDateMap[key]);
                    conflicts.add(b.id);
                } else {
                    roomDateMap[key] = b.id;
                }
            });
        });

        return conflicts;
    }, [bookings]);

    // ⚡ Auto-execute conflict correction on page load if clashes are found
    useEffect(() => {
        if (bookings.length > 0 && conflictBookingIds.size > 0 && !hasAttemptedAutoCorrect && userData) {
            setHasAttemptedAutoCorrect(true);
            handleAutoCorrectClashes(true);
        }
    }, [bookings, conflictBookingIds, userData]);

    const categorizedData = useMemo(() => {
        const today = moment().startOf('day');

        // Step 0: Deduplicate bookings based on customer name, checkin date, room number, and total amount
        const uniqueMap = new Map();
        bookings.forEach(item => {
            const custName = (item.data?.customers?.custname || "").trim().toLowerCase();
            const checkIn = item.data?.checkindate || "";
            const roomNo = (item.data?.rooms || []).map(r => r.roomno).sort().join(",");
            const totalAmount = item.data?.totalamount || 0;
            const bookingSource = (item.data?.bookingsource || "").trim().toLowerCase();

            const uniqueKey = `${custName}_${checkIn}_${roomNo}_${totalAmount}_${bookingSource}`;

            if (!uniqueMap.has(uniqueKey)) {
                uniqueMap.set(uniqueKey, item);
            }
        });

        const deduplicatedBookings = Array.from(uniqueMap.values());

        // Step A: Filter by search text
        const searched = deduplicatedBookings.filter((item) => {
            const customerName = item.data?.customers?.custname || "";
            const mobileNo = item.data?.customers?.mobile || "";
            const bookingSource = item.data?.bookingsource || "";
            const targetRooms = (item.data?.rooms || []).map(r => r.roomno || "").join(" ");
            const combinedSearchString = `${customerName} ${mobileNo} ${bookingSource} ${targetRooms}`.toLowerCase();
            return combinedSearchString.includes(searchText.toLowerCase());
        });

        // Step B: Categorize into buckets (Upcoming requires check-in date strictly greater than today)
        const live = [];
        const upcoming = [];
        const checkout = [];

        searched.forEach(b => {
            const checkIn = moment(b.data.checkindate, 'YYYY-MM-DD').startOf('day');
            const checkOut = moment(b.data.checkoutdate, 'YYYY-MM-DD').startOf('day');
            const isComing = b.data.coming === "Yes";

            if (!isComing) {
                if (checkIn.isAfter(today)) {
                    upcoming.push(b);
                } else if (checkIn.isSameOrBefore(today) && checkOut.isSameOrBefore(today)) {
                    checkout.push(b);
                } else {
                    live.push(b);
                }
            } else {
                if (checkOut.isSameOrBefore(today)) {
                    checkout.push(b);
                } else {
                    live.push(b);
                }
            }
        });

        // Step C: Sort descending by created date
        const sortDesc = (arr) => arr.sort((a, b) => {
            const dateA = new Date(a.createdAt || a.data?.createdAt || 0);
            const dateB = new Date(b.createdAt || b.data?.createdAt || 0);
            return dateB - dateA;
        });

        return {
            live: sortDesc(live),
            upcoming: sortDesc(upcoming),
            checkout: sortDesc(checkout)
        };
    }, [bookings, searchText]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchText, currentTab]);

    const activeDataArray = currentTab === 0 ? categorizedData.live : currentTab === 1 ? categorizedData.upcoming : categorizedData.checkout;

    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = activeDataArray.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(activeDataArray.length / rowsPerPage);

    // ⚡ Core Auto-Correct Logic with Loader Support
    const handleAutoCorrectClashes = async (isBackground = false) => {
        if (conflictBookingIds.size === 0) {
            if (!isBackground) {
                Toast.fire({ icon: 'info', title: 'No room clashes found!' });
            }
            return;
        }

        if (!isBackground) {
            const result = await Swal.fire({
                title: 'Auto-Correct Room Clashes?',
                text: `Found ${conflictBookingIds.size} conflicting reservations. This will automatically reassign overlapping room numbers to the next available sequential rooms.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#2563eb',
                cancelButtonColor: '#64748b',
                confirmButtonText: 'Yes, Auto-Correct!'
            });
            if (!result.isConfirmed) return;
        }

        setIsAutoCorrecting(true);

        try {
            const batch = writeBatch(db);
            const occupiedMap = {};
            bookings.forEach(b => {
                const checkInDate = b.data?.checkindate;
                (b.data?.rooms || []).forEach(r => {
                    if (r.roomno && checkInDate) {
                        if (!occupiedMap[checkInDate]) occupiedMap[checkInDate] = new Set();
                        occupiedMap[checkInDate].add(String(r.roomno));
                    }
                });
            });

            for (const bookingId of conflictBookingIds) {
                const bookingRef = doc(db, "reservations", bookingId);
                const bookingSnap = await getDoc(bookingRef);
                if (!bookingSnap.exists()) continue;

                const bData = bookingSnap.data();
                const checkInDate = bData.checkindate;
                if (!checkInDate) continue;

                if (!occupiedMap[checkInDate]) occupiedMap[checkInDate] = new Set();

                let baseRoomNum = 101;
                while (occupiedMap[checkInDate].has(String(baseRoomNum))) {
                    baseRoomNum++;
                }

                const newRoomNo = String(baseRoomNum);
                occupiedMap[checkInDate].add(newRoomNo);

                const updatedRooms = (bData.rooms || []).map((r, idx) => {
                    if (idx === 0) {
                        return { ...r, roomno: newRoomNo };
                    }
                    return r;
                });

                batch.update(bookingRef, { rooms: updatedRooms });
            }

            await batch.commit();
            Toast.fire({ icon: 'success', title: 'Room clashes successfully auto-corrected!' });
        } catch (error) {
            console.error("Auto-correct error:", error);
            Toast.fire({ icon: 'error', title: 'Failed to auto-correct room clashes', text: error.message });
        } finally {
            setIsAutoCorrecting(false);
        }
    };

    const handleResolveConflict = async (bookingId) => {
        const { value: newRoomNo } = await Swal.fire({
            title: 'Resolve Room Conflict',
            text: 'Enter a new available room number to reassign this booking:',
            input: 'text',
            inputPlaceholder: 'e.g., 103',
            showCancelButton: true,
            confirmButtonColor: '#2563eb',
            cancelButtonColor: '#64748b',
            inputValidator: (value) => {
                if (!value) {
                    return 'You need to write a room number!';
                }
            }
        });

        if (newRoomNo) {
            try {
                const bookingRef = doc(db, "reservations", bookingId);
                const bookingSnap = await getDoc(bookingRef);
                if (!bookingSnap.exists()) return;

                const bookingData = bookingSnap.data();
                const updatedRooms = (bookingData.rooms || []).map((r, idx) => {
                    if (idx === 0) {
                        return { ...r, roomno: newRoomNo };
                    }
                    return r;
                });

                await updateDoc(bookingRef, { rooms: updatedRooms });
                Toast.fire({ icon: 'success', title: 'Room reassigned successfully!' });
            } catch (error) {
                console.error("Reassign error:", error);
                Toast.fire({ icon: 'error', title: 'Failed to update room assignment' });
            }
        }
    };

    const cancelBooking = async (bookingId, roomUsed = "No") => {
        if (!userData) {
            Toast.fire({ icon: "error", title: "Action denied. User context missing." });
            return;
        }

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This will cancel the booking and release all associated rooms!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Yes, cancel it!'
        });

        if (!result.isConfirmed) return;

        try {
            const bookingRef = doc(db, "reservations", bookingId);
            const bookingSnap = await getDoc(bookingRef);

            if (!bookingSnap.exists()) {
                Toast.fire({ icon: "error", title: "Booking record not found." });
                return;
            }

            const bookingData = bookingSnap.data();
            const checkinDetailsQuery = query(collection(db, 'checkindetails'), where('bookingid', '==', bookingId));
            const payHistoryQuery = query(collection(db, 'payhistory'), where('bookingid', '==', bookingId));

            const [checkinDetailsSnapshot, payHistorySnapshot] = await Promise.all([
                getDocs(checkinDetailsQuery),
                getDocs(payHistoryQuery)
            ]);

            const batch = writeBatch(db);

            batch.update(bookingRef, { status: "Canceled" });

            checkinDetailsSnapshot.docs.forEach(item => {
                batch.update(doc(db, "checkindetails", item.id), { status: "Canceled" });
            });
            payHistorySnapshot.docs.forEach(item => {
                batch.update(doc(db, "payhistory", item.id), { status: "Canceled" });
            });
            const rooms = bookingData.rooms || [];
            const logRef = doc(collection(db, 'logs'));
            batch.set(logRef, {
                bookingid: bookingId,
                customerId: bookingData.customerId || "",
                customers: bookingData.customers || {},
                rooms: rooms,
                uid: userData.uid,
                username: username,
                createdAt: Timestamp.now(),
                type: 'Booking Cancellation',
            });

            await batch.commit();
            Toast.fire({ icon: "success", title: 'Reservation Successfully Canceled' });

        } catch (error) {
            console.error("Cancellation system failure detail:", error);
            Toast.fire({
                icon: "error",
                title: 'Failed to process cancellation',
                text: error.message
            });
        }
    };

    const handleCheckIn = async (bookingId) => {
        if (!userData) return;

        const result = await Swal.fire({
            title: 'Check-in Customer?',
            text: "This will mark the customer as arrived and move them to Live Reservations.",
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Yes, Check-in'
        });

        if (result.isConfirmed) {
            try {
                const bookingRef = doc(db, "reservations", bookingId);
                await updateDoc(bookingRef, { coming: "Yes" });

                Toast.fire({ icon: "success", title: 'Customer checked in successfully!' });
                setCurrentTab(0);
            } catch (error) {
                console.error("Check-in error: ", error);
                Toast.fire({
                    icon: "error",
                    title: 'Failed to complete check-in',
                    text: error.message
                });
            }
        }
    };

    return (
        <Box>
            <Menu />

            <Box className="content-wrapper" p={2} sx={{ ml: "260px", backgroundColor: "#f8fafc", minHeight: "100vh", position: "relative" }}>
                
                {/* Loader Overlay when Auto-Correcting */}
                {isAutoCorrecting && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(255, 255, 255, 0.7)",
                            zIndex: 9999,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 2
                        }}
                    >
                        <CircularProgress color="primary" />
                        <Typography variant="body2" fontWeight={600} color="#1e293b">
                            Auto-correcting room clashes... Please wait.
                        </Typography>
                    </Box>
                )}

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5" fontWeight={700} color="#1e293b">
                        View Reservations
                    </Typography>

                    <Button
                        variant="contained"
                        color="warning"
                        startIcon={<AutoFixHigh />}
                        onClick={() => handleAutoCorrectClashes(false)}
                        disabled={isAutoCorrecting}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: "8px",
                            backgroundColor: "#f59e0b",
                            "&:hover": { backgroundColor: "#d97706" }
                        }}
                    >
                        Auto-Correct Room Clashes ({conflictBookingIds.size})
                    </Button>
                </Box>

                <Card sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.05)" }}>
                    <CardContent sx={{ p: 2 }}>

                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                            <Tabs
                                value={currentTab}
                                onChange={(e, newValue) => setCurrentTab(newValue)}
                                variant="fullWidth"
                                indicatorColor="primary"
                                textColor="primary"
                            >
                                <Tab
                                    icon={<Hotel fontSize="small" />}
                                    iconPosition="start"
                                    label={`Currently Live (${categorizedData.live.length})`}
                                    sx={{ fontWeight: 600, textTransform: 'none' }}
                                />
                                <Tab
                                    icon={<EventAvailable fontSize="small" />}
                                    iconPosition="start"
                                    label={`Upcoming Bookings (${categorizedData.upcoming.length})`}
                                    sx={{ fontWeight: 600, textTransform: 'none' }}
                                />
                                <Tab
                                    icon={<ExitToApp fontSize="small" />}
                                    iconPosition="start"
                                    label={`Checkout Alert (${categorizedData.checkout.length})`}
                                    sx={{ fontWeight: 600, textTransform: 'none', color: categorizedData.checkout.length > 0 ? '#ef4444' : 'inherit' }}
                                />
                            </Tabs>
                        </Box>

                        <Box mb={2} maxWidth={360}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search by name, phone, source or room..."
                                value={searchText}
                                onChange={(e) => setsearchText(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        backgroundColor: "#f8fafc",
                                        fontSize: "0.85rem"
                                    }
                                }}
                            />
                        </Box>

                        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "8px", border: "1px solid #f1f5f9" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                                        <TableCell sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Sr</TableCell>
                                        <TableCell sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Action</TableCell>
                                        <TableCell sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Customer</TableCell>
                                        <TableCell sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Booking Source</TableCell>
                                        <TableCell sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Check-in</TableCell>
                                        <TableCell sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Check-out</TableCell>
                                        <TableCell sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Rooms</TableCell>
                                        <TableCell sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Plans</TableCell>
                                        <TableCell sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Adults</TableCell>
                                        <TableCell align="right" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Total</TableCell>
                                        <TableCell align="right" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Paid</TableCell>
                                        <TableCell align="right" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>Remaining</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {currentRows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={12} align="center" sx={{ py: 4, color: "#94a3b8", fontSize: "0.8rem" }}>
                                                No reservations found in this category.
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {currentRows.map((item, index) => {
                                        const hasConflict = conflictBookingIds.has(item.id);

                                        return (
                                            <TableRow key={item.id} hover sx={{ backgroundColor: hasConflict ? "#fef2f2 !important" : "inherit", "&:last-child td, &:last-child th": { border: 0 } }}>
                                                <TableCell sx={{ fontSize: "0.8rem", color: "#64748b" }}>
                                                    {indexOfFirstRow + index + 1}
                                                </TableCell>

                                                <TableCell>
                                                    <Stack direction="row" spacing={0.5}>
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                color="info"
                                                                href={`/reservation-details/${item.id}`}
                                                                sx={{ p: 0.5, backgroundColor: "#e0f2fe", "&:hover": { backgroundColor: "#bae6fd" } }}
                                                            >
                                                                <Visibility sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>

                                                        {hasConflict && (
                                                            <Tooltip title="Room Clash Detected! Click to Reassign Room">
                                                                <IconButton
                                                                    size="small"
                                                                    color="warning"
                                                                    onClick={() => handleResolveConflict(item.id)}
                                                                    sx={{ p: 0.5, backgroundColor: "#fef3c7", color: "#d97706", "&:hover": { backgroundColor: "#fde68a" } }}
                                                                >
                                                                    <SwapHoriz sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}

                                                        {item.data.coming !== "Yes" && (
                                                            <Tooltip title="Check-in Customer">
                                                                <IconButton
                                                                    size="small"
                                                                    color="success"
                                                                    onClick={() => handleCheckIn(item.id)}
                                                                    sx={{ p: 0.5, backgroundColor: "#dcfce7", color: "#16a34a", "&:hover": { backgroundColor: "#bbf7d0" } }}
                                                                >
                                                                    <CheckCircle sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}

                                                        <Tooltip title="Cancel Booking">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => cancelBooking(item.id, "No")}
                                                                sx={{ p: 0.5, backgroundColor: "#fee2e2", "&:hover": { backgroundColor: "#fca5a5" } }}
                                                            >
                                                                <Close sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>

                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Box>
                                                            <Typography fontWeight={600} sx={{ color: "#0f172a", fontSize: "0.8rem" }}>
                                                                {item.data.customers?.custname || "—"}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: "#64748b", display: "block", fontSize: "0.7rem" }}>
                                                                {item.data.customers?.mobile || "—"}
                                                            </Typography>
                                                        </Box>
                                                        {hasConflict && (
                                                            <Tooltip title="Warning: Multiple bookings assigned to the same room on this check-in date!">
                                                                <WarningIcon color="error" sx={{ fontSize: 16 }} />
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </TableCell>

                                                <TableCell sx={{ fontSize: "0.8rem", color: "#334155" }}>
                                                    {item.data.bookingsource || "—"}
                                                </TableCell>

                                                <TableCell sx={{ fontSize: "0.8rem", color: "#334155" }}>
                                                    {item.data.checkindate ? moment(item.data.checkindate).format("DD/MM/YYYY") : "—"}
                                                </TableCell>

                                                <TableCell sx={{ fontSize: "0.8rem", color: "#334155", fontWeight: currentTab === 2 ? 700 : 400, color: currentTab === 2 ? "#dc2626" : "inherit" }}>
                                                    {item.data.checkoutdate ? moment(item.data.checkoutdate).format("DD/MM/YYYY") : "—"}
                                                </TableCell>

                                                <TableCell>
                                                    {(item.data.rooms || []).map((r, i) => (
                                                        <Chip
                                                            key={i}
                                                            label={r.roomno}
                                                            size="small"
                                                            sx={{ mr: 0.5, mb: 0.5, height: "20px", fontSize: "0.7rem", backgroundColor: hasConflict ? "#fee2e2" : "#f1f5f9", color: hasConflict ? "#991b1b" : "inherit", fontWeight: hasConflict ? 700 : 500 }}
                                                        />
                                                    ))}
                                                </TableCell>

                                                <TableCell>
                                                    {(item.data.rooms || []).map((r, i) => (
                                                        <Chip
                                                            key={i}
                                                            label={r.planname}
                                                            size="small"
                                                            sx={{ mr: 0.5, mb: 0.5, height: "20px", fontSize: "0.7rem", backgroundColor: "#e0f2fe", color: "#0369a1", fontWeight: 600 }}
                                                        />
                                                    ))}
                                                </TableCell>

                                                <TableCell sx={{ fontSize: "0.8rem", color: "#334155" }}>
                                                    {item.data.adults || 0}
                                                </TableCell>

                                                <TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#0f172a" }}>
                                                    ₹ {(item.data.totalamount || 0).toFixed(2)}
                                                </TableCell>

                                                <TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#16a34a" }}>
                                                    ₹ {(item.data.advance || 0).toFixed(2)}
                                                </TableCell>

                                                <TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#dc2626" }}>
                                                    ₹ {(item.data.remaining || 0).toFixed(2)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {totalPages > 1 && (
                            <Box display="flex" justifyContent="center" mt={2}>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={(e, v) => setCurrentPage(v)}
                                    color="primary"
                                    size="small"
                                />
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}

export default Viewreservations;